import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: "estate", setTheme: () => {}, companyLogo: "" };
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => localStorage.getItem("theme") || "estate");
  const [companyLogo, setCompanyLogo] = useState("");

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Load user theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const res = await axios.get(`${API}/settings/theme`, { withCredentials: true });
        if (res.data.theme) {
          setThemeState(res.data.theme);
        }
      } catch (err) {
        // Theme load fails silently for unauthenticated users
      }
    };
    loadTheme();
  }, []);

  // Load company logo
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const res = await axios.get(`${API}/company-settings`, { withCredentials: true });
        if (res.data.logo_url) {
          setCompanyLogo(res.data.logo_url);
        }
      } catch (err) {
        // Logo load fails silently for unauthenticated users
      }
    };
    loadLogo();
  }, []);

  const setTheme = useCallback(async (newTheme) => {
    setThemeState(newTheme);
    try {
      await axios.put(`${API}/settings/theme`, { theme: newTheme }, { withCredentials: true });
    } catch (err) {
      // Theme save fails silently — local state already updated
    }
  }, []);

  const updateLogo = useCallback((url) => {
    setCompanyLogo(url);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, companyLogo, updateLogo }}>
      {children}
    </ThemeContext.Provider>
  );
};
